import { IQuiz, QuestionType } from '@quizzach/interfaces';

// Dev-only quiz for the /quiz/:uuid play stage; toggled centrally via
// MOCK_QUIZ_PLAY in mocks.config.ts. A standard-question quiz so every
// play screen (mode selection, intro, questions, answers) renders
// without a logged-in session or purchased quiz.

const QA: Array<[string, string]> = [
	// u/b markup mirrors the editor's emphasis HTML so the play styling
	// for author emphasis stays exercised in dev
	[
		'Koja je <u>najdulja</u> rijeka koja <b>protječe</b> kroz Hrvatsku?',
		'Sava',
	],
	['Koje godine je pao Berlinski zid?', '1989.'],
	['Koji kemijski element ima simbol Au?', 'Zlato'],
	['Tko je naslikao Mona Lisu?', 'Leonardo da Vinci'],
	['Koliko žica ima klasična gitara?', 'Šest'],
	['Koji je glavni grad Australije?', 'Canberra'],
	['Koje je najveće jezero u Africi?', 'Viktorijino jezero'],
	['Tko je autor romana »Zločin i kazna«?', 'F. M. Dostojevski'],
];

export type QuizPlayMockVariant =
	| 'quick'
	| 'premade'
	| 'puzzle'
	| 'chapters'
	| 'kids'
	| 'patterns';

// One real-corpus question per content pattern (utils/contentPattern.ts),
// plus a napomena case and a plain control — the 'patterns' variant
// serves these so every pattern layout is exercised in dev.
const PATTERN_QA: Array<Record<string, unknown>> = [
	{
		standard_question: {
			question_hr:
				'"<i>We all came out to Montreux<br />On the Lake Geneva shoreline<br />To make records with a mobile, yeah<br />We didn&#39;t have much time now</i>"<br />Ovo je početak koje poznate pjesme? Tražimo izvođača i naslov.',
			answer_hr: 'DEEP PURPLE — "SMOKE ON THE WATER"',
			explanation_hr: 'Stihovi opisuju požar u kasinu u Montreuxu 1971.',
		},
	},
	{
		standard_question: {
			question_hr:
				'"<i>You can&#39;t handle the truth!</i>"<br />Koji glumac je to rekao kojem glumcu u kojem filmu?',
			answer_hr: 'JACK NICHOLSON — TOMU CRUISEU, "A FEW GOOD MEN"',
			explanation_hr: 'Scena suđenja iz 1992.',
		},
	},
	{
		// end-positioned quote: the ask precedes the quote (»… izjavio:
		// "…"«) — reading order is kept, the quote renders BELOW the ask
		standard_question: {
			question_hr:
				'Koji je trener, nakon što je njegova momčad primila tri gola u prvom poluvremenu, novinarima izjavio: "<i>Mi ne igramo na rezultat, mi igramo na ljepotu</i>"?',
			answer_hr: 'JÜRGEN KLOPP',
		},
	},
	{
		// mid-text verse block: prose intro, standalone verse, closing ask —
		// lifts in place (intro, verse, ask) instead of jumping to the top
		standard_question: {
			question_hr:
				'Nagrađen je Oscarom 2003. godine za najbolju originalnu pjesmu, ali tom je prilikom nije izveo jer na samu dodjelu nije niti došao. Učinio je to sedamnaest godina kasnije kao potpuno iznenađenje 92. dodjele 2020. godine. Ako još uvijek niste sigurni o kome se radi, evo male pomoći:<br /><br /><i>"Look, if you had one shot, or one opportunity<br />To seize everything you ever wanted, in one moment<br />Would you capture it, or just let it slip?"</i><br /><br />Navedite izvođača i naslov nagrađene pjesme.',
			answer_hr: 'EMINEM, "LOSE YOURSELF"',
			explanation_hr: '',
		},
	},
	{
		// control: quote embedded mid-sentence (prose on both sides) — it
		// must NOT lift out of order, so this stays a plain standard slide
		long_question: true,
		standard_question: {
			question_hr:
				'Ovaj glumac je, prije nego što je preuzeo ikonsku ulogu Maxa Rockatanskog, ručao s Melom Gibsonom kako bi razgovarali o ulozi. Tijekom tog susreta, Gibson mu je rekao: "<i>Jednostavno sam prestar da učinkovito igram Maxa</i>" i osobno mu dao svoj blagoslov za nastavak serijala. Koji je glumac preuzeo ulogu Maxa Rockatanskog, a koja glumica je u filmu "Mad Max: Fury Road" utjelovila lik Furiose?',
			answer_hr: 'TOM HARDY I CHARLIZE THERON',
			explanation_hr: '"Mad Max: Fury Road" iz 2015. režirao je George Miller.',
		},
	},
	{
		standard_question: {
			question_hr:
				'U kojoj su se godini dogodili sljedeći događaji?\n<ul>\n\t<li>nakon 27 godina, Nelson Mandela pušten je iz zatvora</li>\n\t<li>svemirski teleskop Hubble lansiran je u orbitu</li>\n\t<li>ujedinjene su Istočna i Zapadna Njemačka</li>\n\t<li>u Hrvatskoj su održani prvi višestranački izbori</li>\n</ul>',
			answer_hr: '1990.',
			explanation_hr: 'Godina u kojoj se hladnoratovski svijet raspao.',
		},
	},
	{
		points: '2.5',
		points_explanation: '0,5 boda po točnom odgovoru',
		standard_question: {
			question_hr:
				'Navedite brendove koji stoje iza ovih slogana:<br /><strong>A</strong> - Just do it<br /><strong>B</strong> - Taste the feeling<br /><strong>C</strong> - I&#39;m lovin&#39; it<br /><strong>D</strong> - Think different<br /><strong>E</strong> - Because you&#39;re worth it',
			answer_hr:
				'A) NIKE<br />B) COCA-COLA<br />C) McDONALD&#39;S<br />D) APPLE<br />E) L&#39;ORÉAL',
		},
	},
	{
		// intro-less subParts: the lettered rows ARE the whole question
		// (match-up list, the ask lives in the points explanation) — the
		// »Konačan odgovor« tail renders with the letter indicators
		points: '6',
		points_explanation: '1 bod za svaki pogođeni klub i 2 za konačno rješenje',
		standard_question: {
			question_hr:
				'A – <u>Olof Mellberg</u>, <u>Luka Milivojević</u>, Kevin Mirallas, Nery Castillo<br />B – <u>Victor Lindelöf</u>, <u>Raúl Jiménez</u>, Julian Weigl, David Luiz<br />C – <u>Rolando</u>, <u>Kevin Strootman</u>, Cengiz Ünder, Steve Marlet<br />D – <u>Ritsu Doan</u>, <u>Bruma</u>, Thorgan Hazard, Ismail Aissati',
			answer_hr:
				'A — OLYMPIACOS<br />B — BENFICA<br />C — OLYMPIQUE MARSEILLE<br />D — PSV EINDHOVEN<br />KONAČAN ODGOVOR: KONSTANTINOS MITROGLOU',
		},
	},
	{
		standard_question: {
			question_hr:
				'Riješite anagram ako znate da je riječ o hrvatskom sportašu:<br />\n<b>DRKAM OLIĆU</b>',
			answer_hr: 'LUKA MODRIĆ',
		},
	},
	{
		// control: long »tko sam ja« narrative — the story pattern was
		// removed entirely (founder, 2026-08-26), so this must render as a
		// plain standard slide
		long_question: true,
		standard_question: {
			question_hr:
				'Smatran ne samo jednim od najboljih boksača srednje kategorije, već i jednim od najboljih pound-for-pound boksača svih vremena, ovaj je argentinski boksač dominirao 1960-ih i 1970-ih godina. Imao je nevjerojatan osjećaj za distancu, odličnu preciznost i visok boksački IQ, a naslov prvaka srednje kategorije branio je čak četrnaest puta zaredom. Tko je on?',
			answer_hr: 'CARLOS MONZÓN',
			explanation_hr: 'Prvak srednje kategorije 1970.–1977.',
		},
	},
	{
		standard_question: {
			question_hr:
				'Ovo je jedan od klasičnih recepata za - što?<br /><br />Sastojci:<br />20 dag kuhanog slanutka<br />2 žlice tahini paste<br />2 - 3 žlice limunovog soka<br />0,5 dl maslinovog ulja<br />1 - 2 češnja češnjaka<br />sol<br /><br />Priprema:<br />Sve sastojke izmiksati. Po potrebi dodati malo vode kako bi se dobila glatka smjesa.',
			answer_hr: 'HUMUS',
		},
	},
	{
		// ingredients-only recipe: the question becomes the card's label
		standard_question: {
			question_hr:
				'Prepoznajte ovo jelo:<br /><br />140 g guanciale (narezane na trakice)<br />nekoliko kapi maslinovog ulja (za prženje)<br />4 jaja<br />100 g pecorino sira (sitno ribanog)<br />prstohvat soli<br />svježe mljeveni papar<br />340 g špageta',
			answer_hr: 'CARBONARA',
		},
	},
	{
		standard_question: {
			question_hr:
				'Fender, Gibson, Martin, Taylor, Yamaha, Ibanez. Što možete kupiti u svakom od tih dućana?',
			answer_hr: 'GITARU',
		},
	},
	{
		standard_question: {
			question_hr:
				'Poredajte po veličini (od manjeg prema većem) brojeve: 2/3, 11/4, 3/5, 4/5.',
			answer_hr: '3/5, 2/3, 4/5, 11/4',
		},
	},
	{
		standard_question: {
			question_hr:
				'Talijanska uzrečica, ABBA-ina pjesma, svjetski poznat mjuzikl i film iz 2008. Koje dvije kratke riječi tražimo?',
			answer_hr: 'MAMMA MIA',
			explanation_hr:
				'Uzrečica, pjesma iz 1975., mjuzikl iz 1999. i film s Meryl Streep.',
		},
	},
	{
		standard_question: {
			question_hr:
				'Što možemo vidjeti u krošnjama drveća ako znamo da je to ujedno i popularni naziv Olimpijskog stadiona u Pekingu?<br />\n<i>Napomena: Odgovor se sastoji od dvije riječi.</i>',
			answer_hr: 'PTIČJE GNIJEZDO',
			explanation_hr: 'Nacionalni stadion u Pekingu, domaćin OI 2008.',
		},
	},
	{
		standard_question: {
			question_hr:
				'Koja je <u>najdulja</u> rijeka koja <b>protječe</b> kroz Hrvatsku?',
			answer_hr: 'SAVA',
		},
	},
	{
		// »etikete« dossier rows (krug 7 GO: dosje-mreža) — gold Caveat
		// labels in their own column, facts in ink, question below
		standard_question: {
			question_hr:
				'<b>Simptomi</b>: groznica, bolovi u mišićima, glavobolja, grlobolja, kasnije slijede povraćanje, proljev i osip.<br /><b>Prijenos</b>: dodir s krvi ili tjelesnim tekućinama zaražene osobe ili životinje.<br /><b>Inkubacija</b>: dva dana do tri tjedna.<br /><b>Prvi slučajevi</b>: Sudan i Demokratska Republika Kongo 1976. godine.<br /><b>Ime</b>: po rijeci u Kongu blizu koje je identificiran jedan od prvih slučajeva.<br /><br />Kako se zove ova bolest ljudi i primata?',
			answer_hr: 'EBOLA',
			explanation_hr: 'Rijeka Ebola pritoka je rijeke Mongale u DR Kongu.',
		},
	},
	{
		// short quote on an ABCD question (id-corpus: Oppenheimer) — the
		// quote lifts above the question, options render below
		type: QuestionType.abcd,
		abcd_question: {
			text: '"<i>Postao sam Smrt, uništavač svjetova.</i>" Tko je ovo izjavio?',
			a: 'George Foreman',
			b: 'J. Robert Oppenheimer',
			c: 'Julije Cezar',
			d: 'Napoleon I',
			answer: 'B) J. Robert Oppenheimer',
			explanation: 'Citat iz Bhagavad Gite nakon testa Trinity.',
		},
	},
	{
		// sup/sub markup must survive into the answer reveal band
		type: QuestionType.abcd,
		abcd_question: {
			text: 'Čemu je od ponuđenog jednak kvadrat zbroja dva pribrojnika (a + b)<sup>2</sup>?',
			a: 'a<sup>2</sup> + a·b + b<sup>2</sup>',
			b: 'a<sup>2</sup> + 2·a·b + b<sup>2</sup>',
			c: 'a<sup>2</sup> + b<sup>2</sup>',
			d: 'a + a<sup>2</sup>·b<sup>2</sup> + b',
			answer: 'B) a<sup>2</sup> + 2·a·b + b<sup>2</sup>',
		},
	},
	{
		// two correct options — every lettered fragment in the answer gets
		// its own glass-circle indicator, not just the first
		type: QuestionType.abcd,
		points: '1',
		points_explanation: '0,5 boda po točnom odgovoru',
		abcd_question: {
			text: 'Od uspostave Premier League, koja su dva kluba prvi ostvarili to da su u istoj sezoni osvajali neki od kupova, a istovremeno ispali u Championship?',
			a: 'Wigan Athletic',
			b: 'Birmingham City',
			c: 'Portsmouth F.C.',
			d: 'Bradford City',
			answer: 'A) Wigan Athletic<br />B) Birmingham City',
		},
	},
	{
		// long literary excerpt with no attribution verb — the quote marks
		// on the opening italic are the signal (»ulomak iz romana«)
		long_question: true,
		standard_question: {
			question_hr:
				'<i>"Ili je bio lud i mogao je biti pošteđen letenja. Trebao je jedino zatražiti da bude pošteđen, ali čim bi zatražio, ne bi više bio lud i morao bi još izvršavati borbene zadatke. Ili bi bio lud kad bi još izvršavao borbene zadatke, a duševno zdrav kad ih ne bi izvršavao, ali ako je bio zdrav, morao ih je izvršavati. Ako ih je izvršavao, bio je lud pa nije morao, ali ako ih nije htio izvršavati, bio je duševno zdrav i morao je."</i><br /><br />Ovo je ulomak iz romana čiji je naslov postao, i ostao, općeprihvaćeni izraz za frustrirajuće bezizlaznu situaciju, odnosno situaciju bez izbora jer vas svaki izbor vodi samo u apsurd. Kako glasi naziv tog romana?',
			answer_hr: 'KVAKA-22 ("CATCH-22", JOSEPH HELLER)',
		},
	},
	{
		// opening call-to-arms — »Riješi ovaj zadatak« lifts into the
		// handwritten gold kick above the question
		standard_question: {
			question_hr:
				'Riješi ovaj zadatak: Zbrojite broj žica na klasičnoj gitari, broj polja na šahovskoj ploči i broj igrača u vaterpolskoj momčadi. Koji ste broj dobili?',
			answer_hr: '77',
			explanation_hr: '6 + 64 + 7 = 77.',
		},
	},
	{
		// »Tko sam ja?« opener, authored uppercase — the kick keeps the
		// author's casing
		standard_question: {
			question_hr:
				'TKO SAM JA? Rođen sam u Smiljanu 1856. godine, karijeru sam izgradio u Americi, a mjerna jedinica za gustoću magnetskog toka nosi moje ime.',
			answer_hr: 'NIKOLA TESLA',
		},
	},
	{
		// numbered list with a blank (id 1692) — the digits render as the
		// connection-style number indicators, the question prose follows
		// below the list (the outro slot of the numberedList pattern)
		long_question: true,
		standard_question: {
			question_hr:
				'1. oholost<br />\n2. škrtost<br />\n3. bludnost<br />\n4. _______?<br />\n5. neumjerenost u jelu i piću<br />\n6. srditost<br />\n7. lijenost<br />\n<br />\nVjerujemo da ste prepoznali popis sedam smrtnih grijeha kako ih definira katolička kršćanska praksa. Koji smo grijeh izostavili pod brojem 4?',
			answer_hr: 'ZAVIST',
		},
	},
];

export const generateMockQuiz = (
	uuid: string,
	variant: QuizPlayMockVariant
): IQuiz => {
	const baseQa =
		variant === 'patterns'
			? PATTERN_QA.map((overrides, index) => ({
					uuid: `mock-question-${index + 1}`,
					type: QuestionType.standard,
					points: '1',
					level: ((index % 3) + 1) as 1 | 2 | 3,
					country_specific: false,
					...overrides,
				}))
			: null;

	const questions =
		baseQa ??
		(QA.map(([question_hr, answer_hr], index) => ({
			uuid: `mock-question-${index + 1}`,
			type: QuestionType.standard,
			points: '1',
			level: ((index % 3) + 1) as 1 | 2 | 3,
			country_specific: false,
			standard_question: { question_hr, answer_hr },
		})) as Array<Record<string, unknown>>);

	// Every question type is represented so all play layouts are
	// exercised in dev; the visual file is part of the seeded local
	// backend uploads. The 'patterns' variant stays all-standard — its
	// questions exist to exercise the content-pattern layouts instead.
	if (variant !== 'patterns') {
		questions[1] = {
			uuid: 'mock-question-2',
			type: QuestionType.abcd,
			points: '1',
			level: 2,
			country_specific: false,
			points_explanation: 'točan odgovor donosi cijeli bod',
			abcd_question: {
				text: 'Godine 1969. jedan bajkerski film lansirao je Jacka Nicholsona kao novu zvijezdu i pokrenuo pokret Novog Hollywooda. Kako se zove?',
				a: 'Easy Rider',
				b: 'Bonnie and Clyde',
				c: 'The Wild One',
				d: 'Midnight Cowboy',
				answer: 'A) Easy Rider',
				explanation: 'Redateljski prvijenac Dennisa Hoppera.',
			},
		};
		questions[2] = {
			uuid: 'mock-question-3',
			type: QuestionType.visual,
			points: '1',
			level: 3,
			country_specific: false,
			visual_question: {
				text: 'Koja je životinja na fotografiji?<br /><i>Napomena: dovoljan je rodni naziv, vrsta nije potrebna.</i>',
				// abcd-style options: the answer's letter renders with the same
				// glass indicator the abcd question type uses
				a: 'Vidra',
				b: 'Dabar',
				c: 'Nutrija',
				d: 'Kuna',
				answer: 'B) Dabar',
				explanation: '',
				orientation: 'landscape',
				filename: '01-67bf738b7701c.webp',
				// licence fields exercise the credits half of the caption row
				licence_needed: true,
				photo_author_name: 'Thomas Fuhrmann',
				original_photo_url: 'https://example.com/photo',
				licence_name: 'CC BY-SA 4.0',
				licence_link: 'https://creativecommons.org/licenses/by-sa/4.0/',
			},
		};
		questions[3] = {
			uuid: 'mock-question-4',
			type: QuestionType.connection,
			points: '2',
			level: 2,
			country_specific: false,
			points_explanation: '0,5 boda po točnom paru',
			connection_question: {
				text: 'Spoji ime nominiranog ili nagrađenog biografskog filma s opisom osobe na čiju se biografiju referira!\nNapomena: svi filmovi su s popisa nominiranih za Oscara.',
				a: 'Walk the Line',
				b: 'The Imitation Game',
				c: 'Milk',
				d: 'A Beautiful Mind',
				n1: 'prvi otvoreno gay političar u Kaliforniji',
				n2: 'slavni matematičar, nobelovac',
				n3: 'poznati country pjevač',
				n4: 'matematičar koji je "razbio" Enigmu',
				answer_a: 3,
				answer_b: 4,
				answer_c: 1,
				answer_d: 2,
				explanation: '',
			},
		};
		questions[4] = {
			uuid: 'mock-question-5',
			type: QuestionType.association,
			points: '7',
			level: 3,
			country_specific: false,
			points_explanation: 'polje 0,5 · stupac 1 · konačno 2',
			association_question: {
				a1: 'zrno',
				a2: 'aparat',
				a3: 'espresso',
				a4: 'kofein',
				a: 'kava',
				b1: 'krava',
				b2: 'sir',
				b3: 'laktoza',
				b4: 'bijelo',
				b: 'mlijeko',
				c1: 'trska',
				c2: 'kocka',
				c3: 'slatko',
				c4: 'glukoza',
				c: 'šećer',
				d1: 'porculan',
				d2: 'ručka',
				d3: 'tanjurić',
				d4: 'čaj',
				d: 'šalica',
				answer: 'Kavana',
				explanation: 'Sva četiri stupca čekaju te u kavani.',
			},
		};
		questions[6] = {
			uuid: 'mock-question-7',
			type: QuestionType.estimation,
			points: '0',
			level: 2,
			country_specific: false,
			estimation_question: {
				// a/b/c sum — exercises the subParts pattern on estimation slides
				text: 'Zbrojite:<br /><br />a) broj nominacija za Oscara koje je imala Bette Davis<br />b) dob (u godinama) Humphreya Bogarta u trenutku smrti<br />c) trajanje (u minutama) filma "The Ten Commandments" (1956.)',
				answer: '287 (10 + 57 + 220)',
				explanation: '',
			},
		};
	}

	const premade_quiz =
		variant === 'quick'
			? undefined
			: {
					uuid: 'mock-premade',
					title: 'Probni kviz',
					short_description: 'Probni kviz za razvoj play ekrana.',
					long_description: 'Probni kviz za razvoj play ekrana.',
					author: {
						display_name: 'Putoholičari',
						display_name_slug: 'putoholicari',
					},
					playlist_url: 'https://music.youtube.com/playlist?list=mock',
					play_cover_photo: 'premade-11-small-67e5d7dec9cf1.webp',
					level: 1 as const,
					number_of_questions: questions.length,
					answers_template: 'mock-template.pdf',
					puzzle: variant === 'puzzle',
					puzzle_answer: variant === 'puzzle' ? 'Probni odgovor' : '',
					for_kids: variant === 'kids',
					chapters:
						variant === 'chapters'
							? [
									{
										title: 'Prvo poglavlje',
										chapter_start: 1,
										chapter_end: 4,
										description:
											'<p><span style="font-size: 11pt">U ovom poglavlju čekaju te pitanja o <b>glazbi devedesetih</b> — od grungea do europopa.</span></p><div style="text-align: left"><span style="font-size: 11pt">Ovaj je odlomak u uredniku lijevo poravnat i tako mora i ostati.</span></div><p><span style="font-size: 11pt">Primjer: koji je bend objavio album Nevermind? Odgovor: Nirvana. Na odgovore (množina ostaje tinta) se ne čeka dugo.</span></p><p><span style="font-size: 11pt">Primjer niza odgovora: CantoNA – NAbil FekIR – IRska. Konačan odgovor: Konstantinos Mitroglou.</span></p>',
										alternate_description:
											'<p><span style="font-size: 11pt">Ovo je <b>alternativni opis</b> — prikazuje se samo na toku odgovora umjesto originalnog opisa.</span></p>',
									},
									{
										title: 'Drugo poglavlje',
										chapter_start: 5,
										chapter_end: 8,
									},
								]
							: undefined,
				};

	return {
		uuid,
		quiz_type: variant === 'quick' ? 'quick' : 'premade',
		questions,
		number_of_questions: questions.length,
		premade_quiz,
	} as unknown as IQuiz;
};
